import { projectsApi } from '@/api/projectsApi'
import { separateRootProjects } from '@/components/organization/organizationUtils'
import { resolveOrganizationId } from '@/lib/routerUtils'
import { useAuthStore } from '@/stores/authStore'
import type { Project, ProjectCounts, ProjectFilters } from '@/types/project'
import { isOwnerOfOrganization, resolveRootTenantIdFromUser } from '@/utils/permissions'
import { isEqual } from 'lodash-es'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface ProjectsStore {
	// Data
	projects: Project[]
	allProjects: Project[]
	totalCount: number
	currentPage: number
	pageSize: number
	filters: ProjectFilters
	statusCounts: ProjectCounts

	/** Tenant ID of the root project (project whose name matches the org). null when not yet fetched or absent. */
	rootTenantId: string | null

	/** Organization ID the current `projects`/`allProjects`/`rootTenantId` belong to. null until first fetch. */
	loadedOrganizationId: string | null

	isLoading: boolean
	error: string | null

	fetchProjects: (
		filters?: Partial<ProjectFilters>,
		page?: number,
		pageSize?: number,
		organizationIdOverride?: string,
	) => Promise<void>
	fetchProjectDetails: (projectId: string) => Promise<Project | null>
	/** `frontendSettings` (e.g. the serial key) is stored with the tenant row in the same request. */
	createProject: (tenantName: string, frontendSettings?: Record<string, unknown>) => Promise<{ id: string }>
	updateProject: (projectId: string, tenantName: string) => Promise<Project>
	activateProjects: (projectIds: string[]) => Promise<void>
	deactivateProjects: (projectIds: string[]) => Promise<void>
	deleteProjects: (projectIds: string[]) => Promise<void>
	setPage: (page: number) => void
	setPageSize: (size: number) => void
	setFilters: (filters: ProjectFilters) => void
	clearError: () => void
}

// Cancels a stale projects request when a newer one starts (e.g. rapid org switch).
let inFlightProjectsController: AbortController | null = null

export const useProjectsStore = create<ProjectsStore>()(
	devtools(
		(set, get) => ({
			// Initial State
			projects: [],
			allProjects: [],
			totalCount: 0,
			currentPage: 1,
			pageSize: 20,
			filters: {},
			statusCounts: {
				all: 0,
				active: 0,
				deactivated: 0,
			},
			rootTenantId: null,
			loadedOrganizationId: null,
			isLoading: false,
			error: null,

			fetchProjects: async (newFilters, page, pageSize, organizationIdOverride) => {
				// Override needed on org switch: the new id is set before navigate() updates the
				// URL, so resolveOrganizationId() (URL-first) would still return the previous org.
				const organizationId = organizationIdOverride ?? resolveOrganizationId()

				// Org switched: drop the previous org's projects so they don't leak into dropdowns
				// (e.g. reprovision project) — also covers the non-owner early return below.
				const orgChanged = get().loadedOrganizationId !== null && get().loadedOrganizationId !== organizationId
				if (orgChanged) {
					set({
						projects: [],
						allProjects: [],
						totalCount: 0,
						statusCounts: { all: 0, active: 0, deactivated: 0 },
						rootTenantId: null,
						loadedOrganizationId: organizationId,
					})
				}

				const { currentPage, pageSize: currentPageSize, filters: currentFilters } = get()
				const mergedFilters = { ...currentFilters, ...newFilters }
				const targetPage = page ?? currentPage
				const targetPageSize = pageSize ?? currentPageSize

				// Prevent overlapping fetches with the same parameters (but never skip an org change).
				const state = get()
				if (!orgChanged && state.isLoading && isEqual(state.filters, mergedFilters)) {
					return
				}

				// Latest intent wins: cancel any earlier in-flight request (covers the non-owner
				// early return below, which starts no request of its own).
				inFlightProjectsController?.abort()
				inFlightProjectsController = null

				const user = useAuthStore.getState().user
				if (!isOwnerOfOrganization(user, organizationId)) {
					const rootTenantId = resolveRootTenantIdFromUser(user, organizationId)
					set({
						loadedOrganizationId: organizationId,
						// Clear loading flag left by an owner-org fetch aborted above.
						isLoading: false,
						...(rootTenantId && !get().rootTenantId ? { rootTenantId } : {}),
					})
					return
				}

				set({ isLoading: true, error: null, filters: mergedFilters })

				const controller = new AbortController()
				inFlightProjectsController = controller

				try {
					const response = await projectsApi.listProjects(
						organizationId,
						mergedFilters,
						targetPage,
						targetPageSize,
						controller.signal,
					)

					const { data, allData, rootTenantId, adjustedCounts, adjustedTotal } = separateRootProjects(
						response.data,
						response.allData,
						response.counts ?? null,
						response.pagination.total,
					)

					set({
						projects: data,
						allProjects: allData,
						totalCount: adjustedTotal,
						currentPage: targetPage,
						pageSize: targetPageSize,
						statusCounts: adjustedCounts,
						loadedOrganizationId: organizationId,
						isLoading: false,
						error: null,
						...(rootTenantId ? { rootTenantId } : {}),
					})
				} catch (error) {
					// Superseded by a newer fetch — let that request own the state.
					if (controller.signal.aborted) {
						return
					}
					set({
						isLoading: false,
						// Readiness gates (useStateKeys, the grouped filter) key off this, so it has to settle
						// on failure too or they wait forever on projects that will never arrive.
						loadedOrganizationId: organizationId,
						error: error instanceof Error ? error.message : 'Failed to fetch projects',
					})
				} finally {
					if (inFlightProjectsController === controller) {
						inFlightProjectsController = null
					}
				}
			},

			fetchProjectDetails: async (projectId: string) => {
				set({ isLoading: true, error: null })
				try {
					const organizationId = resolveOrganizationId()
					const project = await projectsApi.getProjectDetails(organizationId, projectId)
					set({ isLoading: false, error: null })
					return project
				} catch (error) {
					set({
						isLoading: false,
						error: error instanceof Error ? error.message : 'Failed to fetch project details',
					})
					return null
				}
			},

			createProject: async (tenantName: string, frontendSettings?: Record<string, unknown>) => {
				set({ isLoading: true, error: null })
				try {
					const organizationId = resolveOrganizationId()
					const result = await projectsApi.createProject(organizationId, tenantName, frontendSettings)
					// Refresh projects list
					await get().fetchProjects()
					set({ isLoading: false, error: null })
					// TODO: Remove hardcoded values after backend is updated
					return {
						id: result.id,
						name: tenantName,
						status: 'active' as const,
						users: 0,
						dataSizeClickhouse: '0 GB',
						dataSizeLake: '0 GB',
						createdAt: new Date().toISOString(),
					}
				} catch (error) {
					const errorMessage = (error as { message?: string })?.message || 'Failed to create project'
					set({
						isLoading: false,
						error: errorMessage,
					})
					throw error
				}
			},

			updateProject: async (tenantId: string, tenantName: string) => {
				set({ isLoading: true, error: null })
				try {
					const organizationId = resolveOrganizationId()
					const project = await projectsApi.updateProject(organizationId, tenantId, tenantName)
					// Refresh projects list
					await get().fetchProjects()
					set({ isLoading: false, error: null })
					return project
				} catch (error) {
					set({
						isLoading: false,
						error: error instanceof Error ? error.message : 'Failed to update project',
					})
					throw error
				}
			},

			activateProjects: async (projectIds: string[]) => {
				set({ isLoading: true, error: null })
				try {
					const organizationId = resolveOrganizationId()
					await projectsApi.activateProjects(organizationId, projectIds)
					// Refresh projects list
					await get().fetchProjects()
					set({ isLoading: false, error: null })
				} catch (error) {
					set({
						isLoading: false,
						error: error instanceof Error ? error.message : 'Failed to activate projects',
					})
					throw error
				}
			},

			deactivateProjects: async (projectIds: string[]) => {
				set({ isLoading: true, error: null })
				try {
					const organizationId = resolveOrganizationId()
					await projectsApi.deactivateProjects(organizationId, projectIds)
					// Refresh projects list
					await get().fetchProjects()
					set({ isLoading: false, error: null })
				} catch (error) {
					set({
						isLoading: false,
						error: error instanceof Error ? error.message : 'Failed to deactivate projects',
					})
					throw error
				}
			},

			deleteProjects: async (projectIds: string[]) => {
				set({ isLoading: true, error: null })
				try {
					const organizationId = resolveOrganizationId()
					await projectsApi.deleteProjects(organizationId, projectIds)
					await get().fetchProjects()
					set({ isLoading: false, error: null })
				} catch (error) {
					set({
						isLoading: false,
						error: error instanceof Error ? error.message : 'Failed to delete projects',
					})
					throw error
				}
			},

			setPage: (page: number) => {
				if (page === get().currentPage) {
					return
				}
				set({ currentPage: page })
				get().fetchProjects()
			},

			setPageSize: (size: number) => {
				set({ pageSize: size, currentPage: 1 })
				get().fetchProjects()
			},

			setFilters: (filters: ProjectFilters) => {
				set({ filters, currentPage: 1 })
				get().fetchProjects(filters)
			},

			clearError: () => {
				set({ error: null })
			},
		}),
		{ name: 'projects-store' },
	),
)
